import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { updateCampaignStatus } from '../../../redux/actions/campaign/updateStatus'
import { getElsLookups } from '../../../redux/actions/lookups/lookups'
import { getProfileOverview } from '../../../redux/actions/profile/profile'
import { setReadOnly } from '../../../redux/actions/readOnly/readOnly'
import { EXTERNAL_IN_PROGRESS, RESET_UPDATE_CAMPAIGN_STATUS, UPDATE_PROPERTY } from '../../../redux/actionTypes/actionsTypes'
import { UpdateCampaignState } from '../../../redux/reducers/getCampaign/updateCampaignStatusReducer'
import { GetElsLookupsState } from '../../../redux/reducers/getLookups/getElsLookupsReducer'
import { ProfileOverviewState } from '../../../redux/reducers/getProfile/getProfileOverviewReducer'
import { PropertiesState } from '../../../redux/reducers/getProperties/propertiesReducer'
import CampaignList from '../../../components/CampaignList/CampaignList'
import Button from '../../../components/common/Button/Button'
import LoadingSpinner from '../../../components/common/LoadingSpinner/LoadingSpinner'
import LandingBanner from '../../../components/LandingBanner/LandingBanner'
import Header from '../../../layouts/Header/Header'
import Footer from '../../../layouts/Footer/Footer'
import { isCampaignListDisplayed, isExternalPortalCampaignEligibleForImport, isProfileTypeImportEligible } from '../../../utils/campaignList';
import "./Start.css"
import { formatRoll } from '../../../utils/formatter'
import { selectELSLookups, selectProfileGetOverview, selectProperties, selectUpdateCampaignStatus } from '../../../redux/selectors/exportSelectorPaths';
import trackCustomGA4Event from '../../../utils/GA4EventHelpers/trackCustomGA4Event';

export default function Start(): JSX.Element {
    const { t } = useTranslation('transition')
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const profileId = sessionStorage.getItem("profileId") as string
    const [isReturnTypeSelected, setIsReturnTypeSelected] = useState(false)
    const elsLookupsState: GetElsLookupsState = useSelector(selectELSLookups)
    const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview)
    const propertiesState: PropertiesState = useSelector(selectProperties)
    const updateCampaignStatusState: UpdateCampaignState = useSelector(selectUpdateCampaignStatus)
    const getProfileOverviewDispatched = useRef(false)
    const rollNumber = window.location.href.split('/')[5];
    const [address, setAddress] = useState("")

    useEffect(() => {
        if (!getProfileOverviewDispatched.current &&
            !(profileOverview?.profile?.profile_id === Number(profileId))
        ) {
            dispatch(getProfileOverview(profileId, sessionStorage.getItem("wac") as string))
            getProfileOverviewDispatched.current = true
        }
    }, [dispatch, profileId, profileOverview])

    useEffect(() => {
        if (profileOverview && profileOverview.status && profileOverview.profile.profile_id === Number(profileId)) {
            setAddress(profileOverview.profile.profile_address)
            if (getProfileOverviewDispatched.current) {
                getProfileOverviewDispatched.current = false
            }
            if (profileOverview.status === 200) {
                // Check local storage is poulated before navigating to portal - bug fix for when system navigates to portal before local storage is populated
                const checkProfileDetailsInLocalStoage = (sessionStorage.getItem("rollNumber") && sessionStorage.getItem("profileId") && sessionStorage.getItem("activeCampaignYear")
                    && sessionStorage.getItem("campaignId") && sessionStorage.getItem("campaignStatusCode") && sessionStorage.getItem("currentSnapshotId"));
                if (updateCampaignStatusState.updated && checkProfileDetailsInLocalStoage) {
                    dispatch({ type: RESET_UPDATE_CAMPAIGN_STATUS })
                    navigate('/portal')
                }
            } else {
                if (profileOverview.message) {
                    toast.error(profileOverview.message)
                }
            }
        }
    }, [dispatch, navigate, profileId, profileOverview, updateCampaignStatusState.updated])

    const showImportDataModal = (e: React.MouseEvent) => {
        e.preventDefault()
        trackCustomGA4Event(
            "start_return_decision",
            { start_decision: "import" }
        )
        sessionStorage.setItem("incomeReturnType", "import")
        dispatch(setReadOnly(false))
        setIsReturnTypeSelected(true)

        if (profileOverview.profile.profile_type_code !== "GEV") {
            fetchLookups()
        }
        dispatch(updateCampaignStatus(EXTERNAL_IN_PROGRESS,
            profileId,
            profileOverview.profile.profile_type_code,
            String(profileOverview.profile.current_campaign.campaignId),
            rollNumber,
            "true")) // Set isCopySnapshot to true only when import is selected
    }

    const startNewReturn = (e: React.MouseEvent) => {
        e.preventDefault()
        trackCustomGA4Event(
            "start_return_decision",
            { start_decision: "new" }
        )
        sessionStorage.setItem("incomeReturnType", "new")
        dispatch(setReadOnly(false))
        setIsReturnTypeSelected(true)
        if (profileOverview.profile.profile_type_code !== "GEV") {
            fetchLookups()
        }
        dispatch(updateCampaignStatus(EXTERNAL_IN_PROGRESS,
            profileId,
            profileOverview.profile.profile_type_code,
            String(profileOverview.profile.current_campaign.campaignId),
            rollNumber,
            "false")) // Set isCopySnapshot to false if new is selected
    }

    const fetchLookups = () => {
        // Get lookups if they are not found in localstorage
        if (!elsLookupsState.success && profileOverview.profile.profile_type_code !== "GEV") { // disable ELS calls for GEV portfolio
            const propertyCode = sessionStorage.getItem("propertyCode") ?? ""
            dispatch(getElsLookups(propertyCode));
        }
    }

    useEffect(() => {
        // If profile type code is GEV, system doesn't check for ELS response success before navigating to portal
        if (isReturnTypeSelected && (profileOverview.profile.profile_type_code === "GEV" || elsLookupsState.success)) {
            if (updateCampaignStatusState.updated) {
                // Save current property selection to localstorage for easy access to ids
                const profileId = String(updateCampaignStatusState.campaign["profileId"])
                const campaignId = String(updateCampaignStatusState.campaign["campaignId"])
                const statusCode = String(updateCampaignStatusState.campaign["statusCode"])
                sessionStorage.setItem("rollNumber", sessionStorage.getItem("rollNumber") as string)
                sessionStorage.setItem("profileId", profileId)
                sessionStorage.setItem("campaignId", campaignId)
                sessionStorage.setItem("campaignStatusCode", statusCode);
                sessionStorage.setItem("activeCampaignYear", String(updateCampaignStatusState.campaign['campaignYear']))
                sessionStorage.setItem("currentSnapshotId", String(updateCampaignStatusState.campaign['currentSnapshotId']))
                const profile = propertiesState.profiles.find(profile => String(profile.campaignId) === campaignId)
                if (profile && statusCode && profile.statusCode !== statusCode) {
                    dispatch({ type: UPDATE_PROPERTY, data: { ...profile, "statusCode": statusCode } })
                }
                dispatch(getProfileOverview(profileId, sessionStorage.getItem("wac") as string))
                getProfileOverviewDispatched.current = true
            } else {
                if (updateCampaignStatusState.message) {
                    toast.error(updateCampaignStatusState.message)
                }
            }
        }
    }, [dispatch, elsLookupsState.success, isReturnTypeSelected, updateCampaignStatusState, profileOverview.profile.profile_type_code, propertiesState.profiles])

    return (<>
        <Header showAutoSave={false} isInternalUser={false} />
        <LandingBanner />
        {
            (profileOverview?.profile?.profile_id === Number(profileId)) &&
            <>
                <LoadingSpinner loading={updateCampaignStatusState.loading || profileOverview.loading}>
                    <div className='start-body-container'>
                        <div className='start-body'>
                            <div className='start-heading-section'>
                                {
                                    isProfileTypeImportEligible(profileOverview.profile.profile_type_code) && <>
                                        <h1 className='start-heading'>{t("start.heading")}</h1>
                                        <div className='import-block'>
                                            <p>
                                                {isExternalPortalCampaignEligibleForImport([profileOverview.profile.current_campaign, ...profileOverview.profile.previous_campaigns]) ? t('start.import-question') : t('start.no-import-question')}
                                            </p>
                                        </div>
                                    </>
                                }
                                <div className='roll-info-container'>
                                    <p className="strong roll-num-container">{formatRoll(rollNumber)}</p>
                                    <p className="address-container">{address?.length > 260 ? address?.slice(0, 260) + "..." : address}</p>
                                </div>
                                <div className="transition-btn-group">
                                    <div className="button-group justify-content-center">
                                        {
                                            isExternalPortalCampaignEligibleForImport([profileOverview.profile.current_campaign, ...profileOverview.profile.previous_campaigns]) ?
                                                <>
                                                    <Button onClick={e => showImportDataModal(e)} type="submit" text={t("start.yes")} />
                                                    <Button onClick={e => startNewReturn(e)} type="secondary" text={t("start.no")} />
                                                </>
                                                :
                                                <Button onClick={e => startNewReturn(e)} type="secondary" text={t("start.no-previous-campaigns")} />

                                        }
                                        <Button onClick={() => navigate("/landing")} type="secondary" text={t("return")}
                                            className='start-page-return-btn' />
                                    </div>
                                </div>
                            </div>
                        </div>
                        {
                            isCampaignListDisplayed(profileOverview) &&
                            <div className="campaign-list-wrapper">
                                <div className="campaign-list">
                                    <CampaignList />
                                </div>
                            </div>
                        }
                    </div>
                </LoadingSpinner>
            </>
        }
        <div className="start-footer">
            <Footer />
        </div>
    </>)
}